import { defineStore, storeToRefs } from "pinia";
import { SessionDetailsApi } from "@simbelapp/order-sdk";
import type { ItemType } from "@simbelapp/order-sdk";
import { useSessionDetailsStore } from "./session-details-store";
import type { ISessionsDetailsHabilitationsStore } from "~/utils/interfaces/sessions-interfaces";
import type { IHabilitation } from "~/utils/interfaces/habilitations-interfaces";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useUsersHabilitationsStore } from "~/store/users-habilitations/users-habilitations.store";
import { usePagination } from "~/composables/paginations/usePagination";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";

/**
 * Store for managing session habilitations (certifications/qualifications)
 * Handles CRUD operations and validation for session-level habilitations and attendees' habilitations
 */
export const useSessionDetailsHabilitationsStore = defineStore("sessionDetailsHabilitationsStore", {
  state: (): ISessionsDetailsHabilitationsStore => {
    return {
      sessionAttendeesHabilitations: null,
      sessionHabilitation: null,
      sessionDurationYears: null,
      sessionHabErrors: {
        habilitationError: "",
        durationYearsErrors: "",
      },
      isLoading: false,
      isReadonly: true,
    };
  },
  actions: {
    /**
     * Assign the habilitation from session details to local state
     * Finds the habilitation object from the available list and sets duration
     */
    assignHabilitation(): void {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      if (!sessionDetails.value?.habilitationId) {
        this.sessionHabilitation = null;
        this.sessionDurationYears = null;
        return;
      }

      this.sessionHabilitation = {
        habilitationId: sessionDetails.value?.habilitationId,
        name: sessionDetails.value?.habilitationName ?? "",
        durationYears: sessionDetails.value?.habilitationDurationYears ?? 1,
        searchFields: sessionDetails.value?.habilitationName ?? null,
      };
      this.sessionDurationYears = this.sessionHabilitation.durationYears ?? null;
    },

    /** STATE MANAGEMENT */

    /**
     * Update the selected habilitation and its duration
     * When habilitation is selected, auto-fill duration from habilitation default
     * When cleared, reset both values to null
     */
    updateHabilitationOption(habilitation: IHabilitation | null): void {
      if (habilitation) {
        this.sessionHabilitation = habilitation;
        this.sessionDurationYears = habilitation.durationYears;
      } else {
        this.sessionHabilitation = null;
        this.sessionDurationYears = null;
      }
    },

    /**
     * Toggle between edit and readonly modes
     * When switching to readonly, reassign original values from session details
     */
    toggleEdit(): void {
      this.isReadonly = !this.isReadonly;
      // Reset to original values when exiting edit mode
      this.assignHabilitation();
    },

    /** VALIDATION */

    /**
     * Clear all validation errors
     */
    resetErrors(): void {
      this.sessionHabErrors.durationYearsErrors = "";
      this.sessionHabErrors.habilitationError = "";
    },

    /**
     * Validate habilitation data before saving
     * Ensures both fields are filled together or both are empty
     * @returns true if validation passes, false otherwise
     */
    validateHabilitationData(): boolean {
      this.resetErrors();

      // If habilitation is selected, duration is required
      if (this.sessionHabilitation?.habilitationId && !this.sessionDurationYears) {
        this.sessionHabErrors.durationYearsErrors = "Ce champ est obligatoire";
        return false;
      }

      // If duration is set, habilitation selection is required
      if (!this.sessionHabilitation?.habilitationId && this.sessionDurationYears) {
        this.sessionHabErrors.habilitationError = "Ce champ est obligatoire";
        return false;
      }

      return true;
    },

    /** API OPERATIONS */

    /**
     * Save habilitation changes to the session
     * Only saves if validation passes and data has changed
     * Refreshes session details and attendees habilitations after save
     */
    async saveHabilitation(): Promise<void> {
      if (!this.validateHabilitationData()) {
        return;
      }

      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      const habilitationStore = useUsersHabilitationsStore();
      const paginate = usePagination(PaginationKeysEnum.HABILITATIONS_PAGINATION);
      const paginationOptions = paginate.getFindOptions(paginate.paginateOptions.value);

      // Check if session exists and data has actually changed
      const hasChanged =
        sessionDetails.value?.habilitationId !== this.sessionHabilitation?.habilitationId ||
        sessionDetails.value?.habilitationDurationYears !== this.sessionDurationYears;

      if (sessionDetails.value?.id && hasChanged) {
        this.isLoading = true;
        try {
          // Update session habilitation
          await habilitationStore.updateSessionHabilitation({
            habilitationId: this.sessionHabilitation?.habilitationId ?? null,
            durationYears: this.sessionDurationYears ?? null,
            itemId: sessionDetails.value.id,
            itemType: sessionDetails.value.itemType as ItemType,
          });

          // Refresh session details and attendees habilitations
          await sessionDetailsStore.fetchSessionDetails(sessionDetails.value.id, sessionDetails.value.itemType);
          await this.fetchSessionAttendeesHabilitations(paginationOptions);

          // Exit edit mode
          this.toggleEdit();
        } finally {
          this.isLoading = false;
        }
      }
    },

    /**
     * Fetch habilitations for all attendees of the current session
     * Supports pagination options or fetches all without limit
     * @param paginationOptions - Optional pagination parameters
     */
    async fetchSessionAttendeesHabilitations(paginationOptions?: Record<string, string>): Promise<void> {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      const feedback = useFeedback();

      // Early return if session data is not available
      if (!sessionDetails.value?.id || !sessionDetails.value?.itemType) {
        return;
      }

      // Transform pagination options to camelCase or use default no-limit query
      let transformQuery = {};

      if (paginationOptions) {
        const paginateQuery = new URLSearchParams(paginationOptions);
        transformQuery = Object.fromEntries(paginateQuery.entries());
      } else {
        transformQuery = { noLimit: true };
      }

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
        this.sessionAttendeesHabilitations = await apiInstance.getTrainingPlanAttendeesHabilitations({
          itemType2: sessionDetails.value.itemType,
          itemId: sessionDetails.value.id,
          itemType: sessionDetails.value.itemType,
          ...transformQuery,
        });
      } catch (error) {
        feedback.error("Une erreur est survenue", "small");
        console.error("Error fetching session attendees habilitations:", error);
      }
    },
  },
});
